// src/pages/InventoryLots.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Package, Store, CalendarDays, Wallet, Tag, Info, ChevronLeft,
    CheckSquare, Square, Loader2, List, Hash, Search, Menu, X, Ban,
    Pencil, Save
} from 'lucide-react';
import Modal from '../components/ui/Modal';
import DateInput from '../components/ui/DateInput';
import CheckboxInput from '../components/ui/CheckboxInput';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Dropdown from '../components/ui/Dropdown';
import { useToast } from '../hooks/useToast';
import FastAPIClient from '../services/apiClient';
import { lotsForCatentryByStoreStaff, fetchStores } from '../apiHelper';

const client = new FastAPIClient();

const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true // Set to false if you prefer 24-hour time
    });
};

const InventoryLots = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const listRef = useRef(null);

    const { selectedItem, allItems = [] } = location.state || {};
    const [activeItemId, setActiveItemId] = useState(selectedItem?.catentry_id);
    const [searchTerm, setSearchTerm] = useState('');
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [lotDetails, setLotDetails] = useState([]);
    const [loadingLots, setLoadingLots] = useState(false);
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);

    const [editingLot, setEditingLot] = useState(null);
    const [editForm, setEditForm] = useState({
        unit_cost: '',
        expiration_date: '',
        is_active: true,
        store_id: '',
        post_transactions: true,
    });
    const [saving, setSaving] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const [editingQuantityLot, setEditingQuantityLot] = useState(null);
    const [quantityForm, setQuantityForm] = useState({
        current_quantity: '',
        reason: 'INTERNAL_SHRINKAGE',
        comment: '',
        post_transactions: false,
    });
    const [showQuantityConfirm, setShowQuantityConfirm] = useState(false);

    // Load logged-in user
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast({ message: "Please login to view invoices.", show: true, toastType: 0 });
            navigate("/login");
            return;
        }
        try {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
        } catch {
            showToast({ message: "Invalid session.", show: true, toastType: 2 });
            navigate("/login");
        }
    }, [showToast, navigate]);

    const currentItem = useMemo(() => {
        return allItems.find(item => item.catentry_id === activeItemId) || selectedItem;
    }, [allItems, activeItemId, selectedItem]);

    const loadLotDetails = useCallback(async (itemToLoad) => {
        if (!itemToLoad?.catentry_id || !itemToLoad?.store_id || !user?.users_id) {
            setLotDetails([]);
            return;
        }

        setLoadingLots(true);
        setError(null);

        const payload = {
            users_id: user.users_id,
            store_id: itemToLoad.store_id,
            catentry_id: itemToLoad.catentry_id,
        };

        try {
            // console.log("Fetching lots with payload:", payload);
            const data = await lotsForCatentryByStoreStaff(payload); // Real API call
            // console.log(data)

            // The API returns full Inventorylot objects with nested relations
            // We extract just the lot-level fields we need for display
            const simplifiedLots = data.map(lot => ({
                lot_id: lot.lot_id,
                store_id: lot.store_id,
                store_name: lot.store.identifier,
                receipt_datetime: lot.receipt_datetime,
                unit_cost: lot.unit_cost,
                current_quantity: lot.current_quantity,
                initial_quantity: lot.initial_quantity,
                expiration_date: lot.expiration_date,
                comment: lot.comment || 'N/A',
                goodsreceipt_id: lot.goodsreceipt_id,
                is_active: lot.is_active,
                created_on: lot.created_on,
                updated_on: lot.updated_on,
                // Optional: include reference to goods receipt comment
                receipt_comment: lot.goodsreceipt?.comment || null,
            }));

            setLotDetails(simplifiedLots);
        } catch (err) {
            console.error("Failed to fetch inventory lots:", err);
            setError(err.message || "Failed to load inventory lots.");
            showToast("Failed to load lot details.", 'error');
            setLotDetails([]);
        } finally {
            setLoadingLots(false);
        }
    }, [user, showToast]);

    useEffect(() => {
        if (currentItem) loadLotDetails(currentItem);
    }, [currentItem, loadLotDetails]);

    useEffect(() => {
        if (activeItemId && listRef.current) {
            const el = listRef.current.querySelector(`[data-id="${activeItemId}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [activeItemId]);

    const filteredItems = useMemo(() => {
        if (!searchTerm.trim()) return allItems;
        const lower = searchTerm.toLowerCase();
        return allItems.filter(item =>
            item.catentry.productname.toLowerCase().includes(lower) ||
            (item.catentry.partnumber && item.catentry.partnumber.toLowerCase().includes(lower))
        );
    }, [allItems, searchTerm]);

    const handleItemSelect = useCallback((id) => {
        setActiveItemId(id);
        setMobileSidebarOpen(false);
    }, []);

    const handleEditLot = (lot) => {
        setEditingLot(lot);
        // console.log(lot)
        setEditForm({
            unit_cost: lot.unit_cost || 0,
            expiration_date: lot.expiration_date ? lot.expiration_date.split('T')[0] : '',
            is_active: lot.is_active,
            store_id: lot.store_id,
            post_transactions: true,
        });
    };

    const handleSaveLot = async () => {
        if (!editingLot || !currentItem || !user?.users_id) return;

        // Show confirmation first
        setShowConfirmDialog(true);
    };

    const confirmSaveLot = async () => {
        if (!editingLot || !currentItem || !user?.users_id) return;

        setSaving(true);
        setShowConfirmDialog(false);

        try {
            const payload = {
                lot_id: editingLot.lot_id,
                store_id: parseInt(editForm.store_id) || null,
                users_id: user.users_id,
                unit_cost: parseFloat(editForm.unit_cost) || null,
                expiration_date: editForm.expiration_date || null,
                is_active: editForm.is_active,
                post_transactions: editForm.post_transactions === true,
            };

            // console.log("Updating lot with payload:", payload);

            const response = await client.postData('/inventory/update-inventory-lot', payload);
            // console.log("Update response:", response);

            // Transform the full response back into our simplified format
            const updatedLot = {
                lot_id: response.lot_id,
                store_id: response.store_id,
                store_name: response.store.identifier,
                receipt_datetime: response.receipt_datetime,
                unit_cost: response.unit_cost,
                current_quantity: response.current_quantity,
                initial_quantity: response.initial_quantity,
                expiration_date: response.expiration_date,
                comment: response.comment || 'N/A',
                goodsreceipt_id: response.goodsreceipt_id,
                is_active: response.is_active,
                created_on: response.created_on,
                updated_on: response.updated_on,
                receipt_comment: response.goodsreceipt?.comment || null,
            };

            // Replace the lot in state with the full updated object
            setLotDetails(prev => prev.map(lot =>
                lot.lot_id === updatedLot.lot_id ? updatedLot : lot
            ));

            showToast("Lot updated successfully!", "success");
            setEditingLot(null);
        } catch (err) {
            console.error("Failed to update inventory lot:", err);
            const msg = err.response?.data?.detail || err.message || "Update failed";
            showToast(`Update failed: ${msg}`, "error");
        } finally {
            setSaving(false);
        }
    };

    if (!allItems.length || !currentItem) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Ban size={32} className="mb-4" />
                <p className="text-lg mb-4">No inventory data available for lot viewing.</p>
                <Button onClick={() => navigate('/inventory')} variant="primary">
                    <ChevronLeft size={16} className="mr-2" /> Back to Inventory
                </Button>
            </div>
        );
    }

    const productName = currentItem?.catentry?.productname || 'N/A';
    const partNumber = currentItem?.catentry?.partnumber || 'N/A';
    const storeIdentifier = currentItem?.store?.identifier || 'N/A';
    const currencySymbol = currentItem?.store?.setccurr || '';
    const totalQuantity = currentItem?.current_quantity || 0;
    const unitOfMeasure = currentItem?.catentry?.unitofmeasure?.name || 'Units';

    return (
        <div className="flex flex-col h-[calc(100vh-100px)] bg-gray-100">
            {/* Mobile Header */}
            <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
                <Button variant="ghost" size="sm" onClick={() => navigate('/inventory')}>
                    <ChevronLeft size={20} />
                </Button>
                <h1 className="text-lg font-bold text-text-primary truncate px-4">
                    Lots for: {productName}
                </h1>
                <Button variant="ghost" size="sm" onClick={() => setMobileSidebarOpen(true)}>
                    <Menu size={22} />
                </Button>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Mobile Sidebar Drawer */}
                <div className={`fixed inset-0 z-40 lg:hidden transition-transform duration-300 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setMobileSidebarOpen(false)} />
                    <div className="absolute left-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b border-gray-200">
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                <List size={20} /> Inventory Items ({allItems.length})
                            </h2>
                            <Button variant="ghost" size="sm" onClick={() => setMobileSidebarOpen(false)}>
                                <X size={20} />
                            </Button>
                        </div>
                        <div className="p-4 border-b border-gray-200">
                            <Input
                                type="text"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 text-sm"
                                icon={<Search size={18} className="text-gray-dark absolute left-3 top-1/2 -translate-y-1/2" />}
                            />
                        </div>
                        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-2">
                            {filteredItems.map(item => (
                                <div
                                    key={item.catentry_id}
                                    data-id={item.catentry_id}
                                    onClick={() => handleItemSelect(item.catentry_id)}
                                    className={`p-4 rounded-lg cursor-pointer transition-all border ${item.catentry_id === activeItemId
                                        ? 'bg-gmail-blue text-white border-gmail-blue shadow-md'
                                        : 'hover:bg-gray-50 border-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        {item.catentry_id === activeItemId ? <CheckSquare size={16} /> : <Square size={14} className="text-gray-400" />}
                                        <div>
                                            <p className={`font-bold text-sm ${item.catentry_id === activeItemId ? 'text-white' : 'text-text-primary'}`}>
                                                {item.catentry.productname}
                                            </p>
                                            <p className={`text-xs ${item.catentry_id === activeItemId ? 'text-gray-100' : 'text-text-secondary'}`}>
                                                {item.catentry.partnumber}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                                        <div>
                                            <p className="text-secondary">Quantity</p>
                                            <p className={`font-bold ${item.catentry_id === activeItemId ? 'text-white' : ''}`}>
                                                {item.current_quantity} {item.catentry.unitofmeasure?.name || 'Units'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-secondary">Branch</p>
                                            <p className={item.catentry_id === activeItemId ? 'text-white' : ''}>
                                                {item.store.identifier}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${item.catentry.buyable === 1 ? 'bg-gmail-green text-white' : 'bg-gmail-red/20 text-gmail-red'}`}>
                                            {item.catentry.buyable === 1 ? 'Saleable' : 'Internal Use'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Desktop Sidebar */}
                <div className="hidden lg:block w-full lg:w-1/3 xl:w-1/4">
                    <Card className="h-full bg-white rounded-lg shadow-md flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b border-gray-200">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <List size={20} className="text-gmail-blue" /> Inventory Items ({allItems.length})
                            </h2>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/inventory')}>
                                <ChevronLeft size={20} />
                            </Button>
                        </div>
                        <div className="p-4">
                            <Input
                                type="text"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 text-sm"
                                icon={<Search size={18} className="text-gray-dark absolute left-3 top-1/2 -translate-y-1/2" />}
                            />
                        </div>
                        <div ref={listRef} className="flex-1 overflow-y-auto space-y-2 px-4 pb-4">
                            {filteredItems.map(item => (
                                <div
                                    key={item.catentry_id}
                                    data-id={item.catentry_id}
                                    onClick={() => handleItemSelect(item.catentry_id)}
                                    className={`p-4 rounded-lg cursor-pointer transition-all border ${item.catentry_id === activeItemId
                                        ? 'bg-gmail-blue text-white border-gmail-blue shadow-md'
                                        : 'hover:bg-gray-50 border-gray-200'
                                        }`}
                                >
                                    {/* Same content as mobile */}
                                    <div className="flex items-center gap-3 mb-2">
                                        {item.catentry_id === activeItemId ? <CheckSquare size={16} /> : <Square size={14} className="text-gray-400" />}
                                        <div>
                                            <p className={`font-bold text-sm ${item.catentry_id === activeItemId ? 'text-white' : 'text-text-primary'}`}>
                                                {item.catentry.productname}
                                            </p>
                                            <p className={`text-xs ${item.catentry_id === activeItemId ? 'text-gray-100' : 'text-text-secondary'}`}>
                                                {item.catentry.partnumber}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                                        <div>
                                            <p className="text-secondary">Quantity</p>
                                            <p className={`font-bold ${item.catentry_id === activeItemId ? 'text-white' : ''}`}>
                                                {item.current_quantity} {item.catentry.unitofmeasure?.name || 'Units'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-secondary">Branch</p>
                                            <p className={item.catentry_id === activeItemId ? 'text-white' : ''}>
                                                {item.store.identifier}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${item.catentry.buyable === 1 ? 'bg-gmail-green text-white' : 'bg-gmail-red/20 text-gmail-red'}`}>
                                            {item.catentry.buyable === 1 ? 'Saleable' : 'Internal Use'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                {/* Main Detail Pane */}
                <div className="flex-1 bg-white rounded-lg shadow-md flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                            <div>
                                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary flex items-center gap-3">
                                    <Hash size={28} className="text-gmail-blue" />
                                    Lots for: {productName}
                                </h1>
                                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-text-secondary">
                                    <div className="flex items-center gap-2">
                                        <Package size={16} /> <span className="font-medium text-text-primary">Part No: {partNumber}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Store size={16} /> <span className="font-medium text-text-primary">Branch: {storeIdentifier}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-text-secondary">Total On-Hand Quantity</p>
                                <p className="text-2xl lg:text-3xl font-bold text-gmail-blue">
                                    {totalQuantity} {unitOfMeasure}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4 text-sm text-text-secondary italic">
                            Currently viewing individual inventory lots that make up the total on-hand quantity for this item.
                        </div>
                    </div>

                    {/* Lot Details Content */}
                    <div className="flex-1 overflow-y-auto bg-gray-50 p-4 lg:p-8">
                        <Card className="relative overflow-hidden rounded-lg shadow-lg bg-white flex flex-col min-h-[40vh]">
                            <div className="p-4 border-b flex justify-between items-center">
                                <h2 className="text-xl font-semibold">Individual Inventory Lots ({lotDetails.length})</h2>
                                {loadingLots && <Loader2 size={24} className="animate-spin text-gmail-blue" />}
                            </div>

                            {/* Desktop/Tablet: Full Table */}
                            <div className="hidden md:block flex-1 overflow-auto">
                                <table className="min-w-full text-sm text-gray-800 table-auto">
                                    <thead className="sticky top-0 bg-gray-50 z-10 shadow-sm">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-700 tracking-wide whitespace-nowrap">Lot ID</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-700 tracking-wide whitespace-nowrap">Receipt Date</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-700 tracking-wide whitespace-nowrap">Location</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-700 tracking-wide whitespace-nowrap">Unit Cost</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-700 tracking-wide whitespace-nowrap">Initial Qty</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-700 tracking-wide whitespace-nowrap">Current Qty</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-700 tracking-wide whitespace-nowrap">Expiry Date</th>
                                            {/* <th className="px-4 py-3 text-left font-semibold text-gray-700 tracking-wide whitespace-nowrap">Comment</th> */}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {lotDetails.length === 0 && !loadingLots ? (
                                            <tr>
                                                <td colSpan={6} className="text-center py-20 text-text-secondary">
                                                    <Info size={48} className="mx-auto mb-4 text-gray-400" />
                                                    <p className="text-lg">No active inventory lots found for this product.</p>
                                                </td>
                                            </tr>
                                        ) : (
                                            lotDetails.map((lot, i) => (
                                                <tr key={lot.lot_id || i} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-3 whitespace-nowrap font-medium text-gmail-blue">{lot.lot_id}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(lot.receipt_datetime)}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap">{lot.store_name}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap font-medium">
                                                        {currencySymbol}{lot.unit_cost?.toFixed(2) || '0.00'}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                                                        {lot.initial_quantity} {unitOfMeasure}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap font-bold text-text-primary">
                                                        {lot.current_quantity} {unitOfMeasure}
                                                        {lot.initial_quantity !== lot.current_quantity && (
                                                            <span className="ml-2 text-xs text-red-600">
                                                                ↓ {lot.initial_quantity - lot.current_quantity}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-orange-600">
                                                        {formatDate(lot.expiration_date)}
                                                    </td>
                                                    {/* <td className="px-4 py-3 max-w-xs truncate text-text-secondary" title={lot.comment || lot.receipt_comment || 'N/A'}>
                                                        {lot.comment || lot.receipt_comment || 'N/A'}
                                                    </td> */}
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        {/* <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleEditLot(lot)}
                                                            className="text-gray-500 hover:text-gmail-blue hover:bg-blue-50"
                                                            title="Edit lot"
                                                        >
                                                            <Pencil size={16} />
                                                        </Button> */}
                                                        <Dropdown
                                                            trigger={
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-gray-500 hover:text-gmail-blue hover:bg-blue-50"
                                                                    title="Lot actions"
                                                                >
                                                                    <Pencil size={16} />
                                                                </Button>
                                                            }
                                                            align="right" // or "left" depending on space
                                                            className="w-56"
                                                        >
                                                            <div className="py-1">
                                                                <button
                                                                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                                                    onClick={() => handleEditLot(lot)}
                                                                >
                                                                    <Wallet size={16} className="text-gray-600" />
                                                                    Edit Cost Price
                                                                </button>

                                                                <button
                                                                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                                                    onClick={() => {
                                                                        setEditingQuantityLot(lot);
                                                                        setQuantityForm({
                                                                            current_quantity: lot.current_quantity || 0,
                                                                            reason: 'INTERNAL_SHRINKAGE',
                                                                            comment: '',
                                                                            post_transactions: true,
                                                                        });
                                                                    }}
                                                                >
                                                                    <Package size={16} className="text-gray-600" />
                                                                    Edit Lot Quantity
                                                                </button>

                                                                {/* Optional future items */}
                                                                {/* <button className="...">View Transactions</button> */}
                                                            </div>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile: Card View */}
                            <div className="md:hidden flex-1 overflow-y-auto p-4 space-y-4">
                                {lotDetails.length === 0 && !loadingLots ? (
                                    <div className="text-center py-20 text-text-secondary">
                                        <Info size={48} className="mx-auto mb-4 text-gray-400" />
                                        <p className="text-lg">No active inventory lots found.</p>
                                    </div>
                                ) : (
                                    lotDetails.map((lot, i) => (
                                        <div key={lot.lot_id || i} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div><span className="font-medium text-gray-600">Lot ID:</span></div>
                                                <div className="text-right font-bold text-gmail-blue">{lot.lot_id}</div>

                                                <div><span className="font-medium text-gray-600">Receipt:</span></div>
                                                <div className="text-right">{formatDate(lot.receipt_datetime)}</div>

                                                <div><span className="font-medium text-gray-600">Unit Cost:</span></div>
                                                <div className="text-right font-medium">
                                                    {currencySymbol}{lot.unit_cost?.toFixed(2) || '0.00'}
                                                </div>

                                                {/* Initial Quantity */}
                                                <div><span className="font-medium text-gray-600">Initial Qty:</span></div>
                                                <div className="text-right text-gray-700">
                                                    {lot.initial_quantity} {unitOfMeasure}
                                                </div>

                                                {/* Current Quantity with reduction indicator */}
                                                <div><span className="font-medium text-gray-600">Current Qty:</span></div>
                                                <div className="text-right font-bold text-text-primary">
                                                    {lot.current_quantity} {unitOfMeasure}
                                                    {lot.initial_quantity !== lot.current_quantity && (
                                                        <span className="block text-xs text-red-600 -mt-1">
                                                            ↓ Reduced by {lot.initial_quantity - lot.current_quantity}
                                                        </span>
                                                    )}
                                                </div>

                                                <div><span className="font-medium text-gray-600">Expiry:</span></div>
                                                <div className="text-right text-orange-600">{formatDate(lot.expiration_date)}</div>

                                                <div className="col-span-2">
                                                    <span className="font-medium text-gray-600">Comment:</span>
                                                    <p className="mt-1 text-text-secondary text-xs">
                                                        {lot.comment || lot.receipt_comment || 'N/A'}
                                                    </p>
                                                </div>
                                                {/* edit button */}
                                                <div className="col-span-2 flex justify-end mt-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEditLot(lot)}
                                                        className="text-gray-500 hover:text-gmail-blue"
                                                    >
                                                        <Pencil size={18} />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
            <Modal
                isOpen={!!editingLot}
                onClose={() => setEditingLot(null)}
                title={`Edit Lot #${editingLot?.lot_id}`}
                size="md"
            >
                <div className="space-y-6">
                    <Input
                        label="Cost Price"
                        type="number"
                        step="0.01"
                        value={editForm.unit_cost}
                        onChange={(e) => setEditForm({ ...editForm, unit_cost: e.target.value })}
                        placeholder="0.00"
                        required
                    />

                    <DateInput
                        label="Expiry Date"
                        value={editForm.expiration_date}
                        onChange={(e) => setEditForm({ ...editForm, expiration_date: e.target.value })}
                        placeholder="No expiry"
                    />

                    <CheckboxInput
                        id="is_active"
                        label="Lot is Active"
                        checked={editForm.is_active}
                        onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    />

                    <CheckboxInput
                        id="post_transactions_cost"
                        label="Post transaction record (log cost change)"
                        checked={editForm.post_transactions}
                        onChange={(e) => setEditForm({ ...editForm, post_transactions: e.target.checked })}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="secondary"
                            onClick={() => setEditingLot(null)}
                            disabled={saving}
                        >
                            <X size={16} className="mr-2" /> Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSaveLot}  // ← now opens confirmation
                            disabled={saving}
                        >
                            {saving ? (
                                <>Saving...</>
                            ) : (
                                <>
                                    <Save size={16} className="mr-2" /> Save Changes
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </Modal>
            <Modal
                isOpen={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                title="Confirm Lot Update"
                size="sm"
            >
                <div className="space-y-4">
                    <p className="text-text-primary">
                        You are about to update <strong>Lot #{editingLot?.lot_id}</strong>.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-2">
                        {editForm.unit_cost !== editingLot?.unit_cost && (
                            <div className="flex justify-between">
                                <span className="text-gray-600">Cost Price:</span>
                                <span className="font-medium">
                                    {currencySymbol}{editingLot?.unit_cost?.toFixed(2)} → {currencySymbol}{parseFloat(editForm.unit_cost || 0).toFixed(2)}
                                </span>
                            </div>
                        )}
                        {editForm.expiration_date !== (editingLot?.expiration_date?.split('T')[0] || '') && (
                            <div className="flex justify-between">
                                <span className="text-gray-600">Expiry Date:</span>
                                <span className="font-medium">
                                    {formatDate(editingLot?.expiration_date)} → {editForm.expiration_date ? formatDate(editForm.expiration_date + 'T00:00:00Z') : 'N/A'}
                                </span>
                            </div>
                        )}
                        {editForm.is_active !== editingLot?.is_active && (
                            <div className="flex justify-between">
                                <span className="text-gray-600">Status:</span>
                                <span className="font-medium">
                                    {editingLot?.is_active ? 'Active' : 'Inactive'} → {editForm.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        )}
                        {editForm.post_transactions && (
                            <div className="flex justify-between">
                                <span className="text-gray-600">Post transaction:</span>
                                <span className="font-medium text-green-700">Yes</span>
                            </div>
                        )}
                    </div>
                    <p className="text-sm text-orange-600 font-medium">
                        This action affects inventory valuation and stock availability. It cannot be undone easily.
                    </p>
                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="secondary"
                            onClick={() => setShowConfirmDialog(false)}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={confirmSaveLot}
                            disabled={saving}
                            className="bg-orange-600 hover:bg-orange-700"
                        >
                            {saving ? 'Saving...' : 'Yes, Update Lot'}
                        </Button>
                    </div>
                </div>
            </Modal>
            <Modal
                isOpen={!!editingQuantityLot}
                onClose={() => {
                    setEditingQuantityLot(null);
                    setShowQuantityConfirm(false);
                }}
                title={`Adjust Quantity - Lot #${editingQuantityLot?.lot_id}`}
                size="md"
            >
                <div className="space-y-6">
                    <div className="bg-gray-50 p-4 rounded border border-gray-200">
                        <p className="text-sm text-gray-700 mb-2">
                            Current quantity: <strong>{editingQuantityLot?.current_quantity} {unitOfMeasure}</strong>
                        </p>
                        <p className="text-xs text-gray-500">
                            Changing this value will affect total stock and may trigger an inventory adjustment transaction.
                        </p>
                    </div>

                    <Input
                        label="New Quantity"
                        type="number"
                        step="any"
                        min="0"
                        value={quantityForm.current_quantity}
                        onChange={(e) => setQuantityForm({ ...quantityForm, current_quantity: e.target.value })}
                        placeholder="Enter new quantity"
                        required
                    />

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Adjustment Reason
                        </label>
                        <select
                            required
                            value={quantityForm.reason}
                            onChange={(e) => setQuantityForm({ ...quantityForm, reason: e.target.value })}
                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-gmail-blue"
                        >
                            <option value="INTERNAL_SHRINKAGE">Waste / Loss</option>
                            <option value="INVOICE_OVERSTATED">Supplier invoice overstated (correction)</option>
                            <option value="INTERNAL_STOCK_FOUND">Stock gain / count adjustment</option>
                            <option value="ADDITIONAL_GOODS_RECEIVED">Additional vendor goods</option>
                        </select>
                    </div>

                    <Input
                        label="Adjustment Reason / Comment (optional)"
                        type="text"
                        value={quantityForm.comment}
                        onChange={(e) => setQuantityForm({ ...quantityForm, comment: e.target.value })}
                        placeholder="e.g. Manual correction, damaged goods, miscount..."
                    />

                    {/* New checkbox */}
                    <CheckboxInput
                        id="post_transactions"
                        label="Post transaction (create inventory movement record)"
                        checked={quantityForm.post_transactions !== false} // default true
                        onChange={(e) => setQuantityForm({ ...quantityForm, post_transactions: e.target.checked })}
                    />

                    {/* Disable logic – only for unchanged value or empty/invalid input */}
                    {(() => {
                        const newQtyStr = quantityForm.current_quantity?.toString().trim();
                        const newQty = parseFloat(newQtyStr);
                        const currentQty = editingQuantityLot?.current_quantity ?? 0;

                        const isEmpty = newQtyStr === '' || newQtyStr == null;
                        const isInvalidNumber = isNaN(newQty);
                        const isUnchanged = !isNaN(newQty) && newQty === currentQty;

                        const isDisabled = isEmpty || isInvalidNumber || isUnchanged;

                        return (
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <Button
                                    variant="secondary"
                                    onClick={() => setEditingQuantityLot(null)}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    variant="primary"
                                    className="bg-orange-600 hover:bg-orange-700"
                                    onClick={() => setShowQuantityConfirm(true)}
                                    disabled={isDisabled || saving}
                                    title={
                                        isUnchanged
                                            ? "Quantity is the same – no change needed"
                                            : (isEmpty || isInvalidNumber)
                                                ? "Please enter a valid quantity"
                                                : ""
                                    }
                                >
                                    Review & Confirm
                                </Button>
                            </div>
                        );
                    })()}
                </div>
            </Modal>

            <Modal
                isOpen={showQuantityConfirm}
                onClose={() => setShowQuantityConfirm(false)}
                title="Confirm Quantity Adjustment"
                size="sm"
            >
                <div className="space-y-4">
                    <p>
                        You are about to change Lot #{editingQuantityLot?.lot_id} quantity from
                        <strong> {editingQuantityLot?.current_quantity} </strong>
                        to
                        <strong> {quantityForm.current_quantity} {unitOfMeasure}</strong>.
                    </p>

                    {quantityForm.comment && (
                        <div className="bg-gray-50 p-3 rounded text-sm">
                            <p className="font-medium">Reason:</p>
                            <p className="text-gray-700">{quantityForm.comment}</p>
                        </div>
                    )}

                    <div className="bg-gray-50 p-3 rounded text-sm">
                        <p className="font-medium">Post transaction record:</p>
                        <p className={quantityForm.post_transactions !== false ? "text-green-700" : "text-amber-700"}>
                            {quantityForm.post_transactions !== false ? "Yes (recommended)" : "No – only update quantity"}
                        </p>
                    </div>

                    <p className="text-sm text-orange-600 font-medium">
                        This action will be logged and may affect cost accounting and reporting.
                    </p>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="secondary"
                            onClick={() => setShowQuantityConfirm(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            className="bg-orange-600 hover:bg-orange-700"
                            onClick={async () => {
                                if (!editingQuantityLot) return;

                                setSaving(true);
                                setShowQuantityConfirm(false);

                                try {
                                    const payload = {
                                        lot_id: editingQuantityLot.lot_id,
                                        store_id: editingQuantityLot.store_id,
                                        users_id: user.users_id,
                                        current_quantity: parseFloat(quantityForm.current_quantity),
                                        reason: quantityForm.reason,
                                        comment: quantityForm.comment || null,
                                        post_transactions: quantityForm.post_transactions !== false, // default true
                                    };

                                    // console.log(payload)

                                    const response = await client.postData('/inventory/update-lot-quantity', payload);

                                    // Refresh lots
                                    await loadLotDetails(currentItem);

                                    showToast("Quantity adjusted successfully", "success");
                                    setEditingQuantityLot(null);
                                } catch (err) {
                                    const msg = err.response?.data?.detail || "Adjustment failed";
                                    showToast(`Failed: ${msg}`, "error");
                                } finally {
                                    setSaving(false);
                                }
                            }}
                            disabled={saving}
                        >
                            {saving ? 'Processing...' : 'Confirm Adjustment'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default InventoryLots;